import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Lock, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { cart, cartTotal, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [shippingAddress, setShippingAddress] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    phone: ''
  });

  const shippingCost = cartTotal >= 150 ? 0 : 9.90;
  const totalWithShipping = cartTotal + shippingCost;

  const handleAddressChange = (e) => {
    setShippingAddress({ ...shippingAddress, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const required = ['firstName', 'lastName', 'address', 'city', 'postalCode', 'phone'];
    for (const field of required) {
      if (!shippingAddress[field].trim()) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return false;
      }
    }
    return true;
  };

  const handleStripeCheckout = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
      setLoading(false);
    }
  };

  const handlePayPalCheckout = async () => {
    if (!validateForm()) return;
    
    toast.info('PayPal sera disponible prochainement');
  };

  const handleSubmit = () => {
    if (paymentMethod === 'stripe') {
      handleStripeCheckout();
    } else {
      handlePayPalCheckout();
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth?redirect=checkout');
    } else if (cart.length === 0) {
      navigate('/cart');
    }
  }, [isAuthenticated, cart.length, navigate]);

  // Snapshot du panier pour la relance d'abandon (visible en back-office).
  // Declenche a l'arrivee sur le paiement = forte intention d'achat. Le snapshot
  // est purge cote serveur a la creation de la commande (order.created), il ne
  // reste donc que les paniers reellement abandonnes.
  useEffect(() => {
    if (!isAuthenticated || !user?.email || cart.length === 0) return;
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    axios
      .post(`${API}/cart/snapshot`, {
        email: user.email,
        name,
        items: cart,
        total: totalWithShipping,
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.email, cart.length]);

  if (!isAuthenticated || cart.length === 0) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="checkout-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="py-12 border-b border-black/10 mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl md:text-6xl tracking-tight"
          >
            Checkout
          </motion.h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Shipping Address */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 md:p-8 border border-black/10"
            >
              <h2 className="font-heading text-2xl mb-6">Adresse de livraison</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={shippingAddress.firstName}
                    onChange={handleAddressChange}
                    required
                    className="w-full bg-transparent border border-black/20 py-3 px-4 focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-shipping-firstname"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={shippingAddress.lastName}
                    onChange={handleAddressChange}
                    required
                    className="w-full bg-transparent border border-black/20 py-3 px-4 focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-shipping-lastname"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                    Adresse *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={shippingAddress.address}
                    onChange={handleAddressChange}
                    required
                    className="w-full bg-transparent border border-black/20 py-3 px-4 focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-shipping-address"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                    Ville *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={shippingAddress.city}
                    onChange={handleAddressChange}
                    required
                    className="w-full bg-transparent border border-black/20 py-3 px-4 focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-shipping-city"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                    Code postal *
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={shippingAddress.postalCode}
                    onChange={handleAddressChange}
                    required
                    className="w-full bg-transparent border border-black/20 py-3 px-4 focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-shipping-postal"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                    Pays
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={shippingAddress.country}
                    onChange={handleAddressChange}
                    className="w-full bg-transparent border border-black/20 py-3 px-4 focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-shipping-country"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={shippingAddress.phone}
                    onChange={handleAddressChange}
                    required
                    className="w-full bg-transparent border border-black/20 py-3 px-4 focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-shipping-phone"
                  />
                </div>
              </div>
            </motion.div>

            {/* Payment Method */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 md:p-8 border border-black/10"
            >
              <h2 className="font-heading text-2xl mb-6">Méthode de paiement</h2>
              
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('stripe')}
                  className={`w-full flex items-center gap-4 p-4 border transition-colors ${
                    paymentMethod === 'stripe' 
                      ? 'border-gold bg-gold/10' 
                      : 'border-black/20 hover:border-black/40'
                  }`}
                  data-testid="payment-stripe"
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'stripe' ? 'border-gold' : 'border-black/40'
                  }`}>
                    {paymentMethod === 'stripe' && <Check size={12} className="text-gold" />}
                  </div>
                  <CreditCard size={20} />
                  <span>Carte bancaire (Stripe)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMethod('paypal')}
                  className={`w-full flex items-center gap-4 p-4 border transition-colors ${
                    paymentMethod === 'paypal' 
                      ? 'border-gold bg-gold/10' 
                      : 'border-black/20 hover:border-black/40'
                  }`}
                  data-testid="payment-paypal"
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'paypal' ? 'border-gold' : 'border-white/40'
                  }`}>
                    {paymentMethod === 'paypal' && <Check size={12} className="text-gold" />}
                  </div>
                  <span className="font-bold text-blue-500">Pay</span>
                  <span className="font-bold text-blue-700">Pal</span>
                </button>
              </div>

              <div className="flex items-center gap-2 mt-6 text-sm text-foreground/50">
                <Lock size={14} />
                <span>Paiement 100% sécurisé</span>
              </div>
            </motion.div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 md:p-8 sticky top-28 border border-black/10"
              data-testid="checkout-summary"
            >
              <h2 className="font-heading text-2xl mb-6">Récapitulatif</h2>

              {/* Items */}
              <div className="space-y-4 pb-6 border-b border-black/10 max-h-60 overflow-y-auto">
                {cart.map((item) => (
                  <div key={`${item.product_id}-${item.size}-${item.color}`} className="flex gap-4">
                    <div className="w-16 h-20 bg-black/10 flex-shrink-0">
                      <img
                        src={item.product?.images?.[0]}
                        alt={item.product?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{item.product?.name}</p>
                      <p className="text-xs text-foreground/50">{item.size} • {item.color}</p>
                      <p className="text-xs text-foreground/50">Qté: {item.quantity}</p>
                    </div>
                    <p className="text-sm text-gold">
                      {((item.product?.price || 0) * item.quantity).toFixed(2)} €
                    </p>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-4 py-6 border-b border-black/10">
                <div className="flex justify-between text-foreground/60">
                  <span>Sous-total</span>
                  <span>{cartTotal.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-foreground/60">
                  <span>Livraison</span>
                  <span>{shippingCost === 0 ? 'Gratuite' : `${shippingCost.toFixed(2)} €`}</span>
                </div>
              </div>

              <div className="flex justify-between mt-6 mb-8">
                <span className="font-heading text-lg">Total</span>
                <span className="price-tag text-gold text-xl" data-testid="checkout-total">
                  {totalWithShipping.toFixed(2)} €
                </span>
              </div>

              <button
                onClick={handleSubmit}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="place-order-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Payer {totalWithShipping.toFixed(2)} €
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
